// src/pages/TikpagExperience/TikpagExperienceExperiencias.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Edit3,
  Gift,
  Image,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X
} from "lucide-react";

import axios from "../../axiosConfig";
import "./TikpagExperienceExperiencias.css";

const FORM_INICIAL = {
  id: null,
  categoria_id: "",
  titulo: "",
  descricao: "",
  instrucoes: "",
  imagem_url: "",
  imagem: null,
  fornecedor: "",
  valor_referencia: "",
  quantidade_disponivel: "",
  validade_dias: "",
  ativo: true,
  pins: []
};

const formatarDinheiro = (valor) => {
  if (
    valor === null ||
    valor === undefined ||
    valor === ""
  ) {
    return "-";
  }

  return Number(valor).toLocaleString(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL"
    }
  );
};

export default function TikpagExperienceExperiencias() {
  const [experiencias, setExperiencias] =
    useState([]);

  const [categorias, setCategorias] =
    useState([]);

  const [campanhas, setCampanhas] =
    useState([]);

  const [campanhaId, setCampanhaId] =
    useState("");

  const [pins, setPins] =
    useState([]);

  const [busca, setBusca] =
    useState("");

  const [categoriaFiltro, setCategoriaFiltro] =
    useState("");

  const [pinFiltro, setPinFiltro] =
    useState("");

  const [statusFiltro, setStatusFiltro] =
    useState("");

  const [formulario, setFormulario] =
    useState(FORM_INICIAL);

  const [modalAberto, setModalAberto] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [salvando, setSalvando] =
    useState(false);

  const [
    enviandoImagem,
    setEnviandoImagem
  ] = useState(false);

  const [erro, setErro] =
    useState("");

  const [sucesso, setSucesso] =
    useState("");

  const carregarBase = useCallback(async () => {
    try {
      const [
        categoriasResposta,
        campanhasResposta
      ] = await Promise.all([
        axios.get(
          "/admin/experience/categorias"
        ),
        axios.get(
          "/admin/experience/campanhas"
        )
      ]);

      const listaCategorias =
        Array.isArray(
          categoriasResposta.data?.categorias
        )
          ? categoriasResposta.data.categorias
          : [];

      const listaCampanhas =
        Array.isArray(
          campanhasResposta.data?.campanhas
        )
          ? campanhasResposta.data.campanhas
          : [];

      setCategorias(listaCategorias);
      setCampanhas(listaCampanhas);

      setCampanhaId((atual) => {
        if (atual) return atual;

        return listaCampanhas.length
          ? String(
              listaCampanhas[0].id
            )
          : "";
      });
    } catch (err) {
      console.error(
        "Erro ao carregar base das experiências:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar categorias e campanhas."
      );
    }
  }, []);

  const carregarPins = useCallback(
    async (idCampanha) => {
      if (!idCampanha) {
        setPins([]);
        return;
      }

      try {
        const resposta = await axios.get(
          `/admin/experience/campanhas/${idCampanha}/pins`
        );

        setPins(
          Array.isArray(
            resposta.data?.pins
          )
            ? resposta.data.pins
            : []
        );
      } catch (err) {
        console.error(
          "Erro ao carregar PINs das experiências:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar os PINs."
        );
      }
    },
    []
  );

  const carregarExperiencias =
    useCallback(async () => {
      setLoading(true);
      setErro("");

      try {
        const resposta = await axios.get(
          "/admin/experience/experiencias"
        );

        setExperiencias(
          Array.isArray(
            resposta.data?.experiencias
          )
            ? resposta.data.experiencias
            : []
        );
      } catch (err) {
        console.error(
          "Erro ao carregar experiências:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar as experiências."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    carregarBase();
    carregarExperiencias();
  }, [
    carregarBase,
    carregarExperiencias
  ]);

  useEffect(() => {
    carregarPins(campanhaId);
    setPinFiltro("");
  }, [
    campanhaId,
    carregarPins
  ]);

  const experienciasFiltradas =
    useMemo(() => {
      const termo =
        busca.trim().toLowerCase();

      return experiencias.filter(
        (experiencia) => {
          const correspondeBusca =
            !termo ||
            [
              experiencia.titulo,
              experiencia.descricao,
              experiencia.fornecedor,
              experiencia.categoria_nome,
              ...(Array.isArray(
                experiencia.pins
              )
                ? experiencia.pins.map(
                    (pin) => pin.nome
                  )
                : [])
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(termo);

          const correspondeCategoria =
            !categoriaFiltro ||
            String(
              experiencia.categoria_id ||
                ""
            ) ===
              String(
                categoriaFiltro
              );

          const correspondePin =
            !pinFiltro ||
            (
              Array.isArray(
                experiencia.pins
              ) &&
              experiencia.pins.some(
                (pin) =>
                  String(pin.id) ===
                  String(pinFiltro)
              )
            );

          const correspondeStatus =
            !statusFiltro ||
            (
              statusFiltro === "ATIVA"
                ? Boolean(
                    experiencia.ativo
                  )
                : !Boolean(
                    experiencia.ativo
                  )
            );

          return (
            correspondeBusca &&
            correspondeCategoria &&
            correspondePin &&
            correspondeStatus
          );
        }
      );
    }, [
      experiencias,
      busca,
      categoriaFiltro,
      pinFiltro,
      statusFiltro
    ]);

  const abrirNovaExperiencia = () => {
    setFormulario(FORM_INICIAL);
    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const abrirEdicao = async (
    experiencia
  ) => {
    setErro("");
    setSucesso("");

    try {
      const resposta = await axios.get(
        `/admin/experience/experiencias/${experiencia.id}`
      );

      const detalhe =
        resposta.data?.experiencia ||
        experiencia;

      const pinsDetalhe =
        Array.isArray(
          resposta.data?.pins
        )
          ? resposta.data.pins
          : [];

      setFormulario({
        id: Number(detalhe.id),
        categoria_id:
          detalhe.categoria_id
            ? String(
                detalhe.categoria_id
              )
            : "",
        titulo:
          detalhe.titulo || "",
        descricao:
          detalhe.descricao || "",
        instrucoes:
          detalhe.instrucoes || "",
        imagem_url:
          detalhe.imagem_url || "",
        imagem: null,
        fornecedor:
          detalhe.fornecedor || "",
        valor_referencia:
          detalhe.valor_referencia ??
          "",
        quantidade_disponivel:
          detalhe
            .quantidade_disponivel ??
          "",
        validade_dias:
          detalhe.validade_dias ??
          "",
        ativo:
          Boolean(detalhe.ativo),
        pins:
          pinsDetalhe.map(
            (pin) => Number(pin.id)
          )
      });

      setModalAberto(true);
    } catch (err) {
      console.error(
        "Erro ao carregar experiência para edição:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar a experiência."
      );
    }
  };

  const fecharModal = () => {
    if (salvando) return;

    setModalAberto(false);
    setFormulario(FORM_INICIAL);
  };

  const atualizarCampo = (
    campo,
    valor
  ) => {
    setFormulario((atual) => ({
      ...atual,
      [campo]: valor
    }));
  };





  const selecionarImagem =
  async (event) => {
    const arquivo =
      event.target.files?.[0];

    if (!arquivo) return;

    setErro("");
    setEnviandoImagem(true);

    try {
      const dadosImagem =
        new FormData();

      dadosImagem.append(
        "imagem",
        arquivo
      );

      const resposta =
        await axios.post(
          "/admin/experience/upload-imagem",
          dadosImagem,
          {
            headers: {
              "Content-Type":
                "multipart/form-data"
            }
          }
        );

      const imagemUrl =
        resposta.data?.imagem_url;

      if (!imagemUrl) {
        throw new Error(
          "URL da imagem não retornada."
        );
      }

      setFormulario(
        (atual) => ({
          ...atual,
          imagem_url:
            imagemUrl
        })
      );
    } catch (err) {
      console.error(
        "Erro ao enviar imagem da experiência:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível enviar a imagem."
      );
    } finally {
      setEnviandoImagem(false);
    }
  };

  const alternarPin = (pinId) => {
    setFormulario((atual) => {
      const selecionado =
        atual.pins.includes(pinId);

      return {
        ...atual,
        pins: selecionado
          ? atual.pins.filter(
              (id) => id !== pinId
            )
          : [
              ...atual.pins,
              pinId
            ]
      };
    });
  };

  const salvarExperiencia = async (
    event
  ) => {
    event.preventDefault();

    if (!formulario.titulo.trim()) {
      setErro(
        "Informe o título da experiência."
      );
      return;
    }

    if (!formulario.pins.length) {
      setErro(
        "Vincule a experiência a pelo menos um PIN."
      );
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const payload = {
        categoria_id:
          formulario.categoria_id
            ? Number(
                formulario.categoria_id
              )
            : null,

        titulo:
          formulario.titulo.trim(),

        descricao:
          formulario.descricao.trim(),

        instrucoes:
          formulario.instrucoes.trim(),

        imagem_url:
          formulario.imagem_url.trim(),

        fornecedor:
          formulario.fornecedor.trim(),

        valor_referencia:
          formulario.valor_referencia ===
          ""
            ? null
            : Number(
                formulario
                  .valor_referencia
              ),

        quantidade_disponivel:
          formulario
            .quantidade_disponivel ===
          ""
            ? null
            : Number(
                formulario
                  .quantidade_disponivel
              ),

        validade_dias:
          formulario.validade_dias ===
          ""
            ? null
            : Number(
                formulario
                  .validade_dias
              ),

        ativo:
          Boolean(formulario.ativo),

        pins:
          formulario.pins.map(Number)
      };

      if (formulario.id) {
        await axios.put(
          `/admin/experience/experiencias/${formulario.id}`,
          payload
        );

        setSucesso(
          "Experiência atualizada com sucesso."
        );
      } else {
        await axios.post(
          "/admin/experience/experiencias",
          payload
        );

        setSucesso(
          "Experiência criada com sucesso."
        );
      }

      setModalAberto(false);
      setFormulario(FORM_INICIAL);

      await carregarExperiencias();
    } catch (err) {
      console.error(
        "Erro ao salvar experiência:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível salvar a experiência."
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluirExperiencia = async (
    experiencia
  ) => {
    const confirmou =
      window.confirm(
        `Excluir a experiência "${experiencia.titulo}"?`
      );

    if (!confirmou) return;

    setErro("");
    setSucesso("");

    try {
      await axios.delete(
        `/admin/experience/experiencias/${experiencia.id}`
      );

      setSucesso(
        "Experiência excluída com sucesso."
      );

      await carregarExperiencias();
    } catch (err) {
      console.error(
        "Erro ao excluir experiência:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível excluir a experiência."
      );
    }
  };

  const totalEscolhas =
    experiencias.reduce(
      (total, experiencia) =>
        total +
        Number(
          experiencia
            .quantidade_escolhas || 0
        ),
      0
    );

  const totalVinculos =
    experiencias.reduce(
      (total, experiencia) =>
        total +
        Number(
          experiencia
            .quantidade_pins || 0
        ),
      0
    );

  return (
    <div className="experienceExperiencias-page">
      <header className="experienceExperiencias-header">
        <div>
          <span className="experienceExperiencias-kicker">
            Tikpag Experience
          </span>

          <h1>Experiências</h1>

          <p>
            Cadastre as opções de premiação e
            vincule cada experiência a um ou mais
            PINs.
          </p>
        </div>

        <button
          type="button"
          className="experienceExperiencias-primaryButton"
          onClick={abrirNovaExperiencia}
        >
          <Plus size={16} />
          Nova experiência
        </button>
      </header>

      {erro && (
        <div className="experienceExperiencias-alert erro">
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="experienceExperiencias-alert sucesso">
          {sucesso}
        </div>
      )}

      <section className="experienceExperiencias-summary">
        <article className="experienceExperiencias-summaryCard">
          <div className="experienceExperiencias-summaryIcon">
            <Gift size={20} />
          </div>

          <div>
            <span>
              Experiências cadastradas
            </span>

            <strong>
              {experiencias.length}
            </strong>
          </div>
        </article>

        <article className="experienceExperiencias-summaryCard">
          <div className="experienceExperiencias-summaryIcon">
            <Image size={20} />
          </div>

          <div>
            <span>
              Vínculos com PINs
            </span>

            <strong>
              {totalVinculos}
            </strong>
          </div>
        </article>

        <article className="experienceExperiencias-summaryCard">
          <div className="experienceExperiencias-summaryIcon">
            <Gift size={20} />
          </div>

          <div>
            <span>
              Escolhas realizadas
            </span>

            <strong>
              {totalEscolhas}
            </strong>
          </div>
        </article>
      </section>

      <section className="experienceExperiencias-panel">
        <div className="experienceExperiencias-toolbar">
          <div className="experienceExperiencias-search">
            <Search size={16} />

            <input
              value={busca}
              onChange={(event) =>
                setBusca(
                  event.target.value
                )
              }
              placeholder="Buscar experiência, categoria ou fornecedor"
            />
          </div>

          <div className="experienceExperiencias-filters">
            <select
              value={campanhaId}
              onChange={(event) =>
                setCampanhaId(
                  event.target.value
                )
              }
            >
              {!campanhas.length && (
                <option value="">
                  Nenhuma campanha
                </option>
              )}

              {campanhas.map(
                (campanha) => (
                  <option
                    key={campanha.id}
                    value={campanha.id}
                  >
                    {campanha.nome}
                  </option>
                )
              )}
            </select>

            <select
              value={categoriaFiltro}
              onChange={(event) =>
                setCategoriaFiltro(
                  event.target.value
                )
              }
            >
              <option value="">
                Todas as categorias
              </option>

              {categorias.map(
                (categoria) => (
                  <option
                    key={categoria.id}
                    value={categoria.id}
                  >
                    {categoria.nome}
                  </option>
                )
              )}
            </select>

            <select
              value={pinFiltro}
              onChange={(event) =>
                setPinFiltro(
                  event.target.value
                )
              }
            >
              <option value="">
                Todos os PINs
              </option>

              {pins.map((pin) => (
                <option
                  key={pin.id}
                  value={pin.id}
                >
                  {pin.nome}
                </option>
              ))}
            </select>

            <select
              value={statusFiltro}
              onChange={(event) =>
                setStatusFiltro(
                  event.target.value
                )
              }
            >
              <option value="">
                Todas
              </option>

              <option value="ATIVA">
                Ativas
              </option>

              <option value="INATIVA">
                Inativas
              </option>
            </select>

            <button
              type="button"
              className="experienceExperiencias-secondaryButton"
              onClick={
                carregarExperiencias
              }
              disabled={loading}
            >
              <RefreshCcw
                size={16}
                className={
                  loading
                    ? "experienceExperiencias-spin"
                    : ""
                }
              />

              Atualizar
            </button>
          </div>
        </div>

        <div className="experienceExperiencias-list">
          {experienciasFiltradas.map(
            (experiencia) => (
              <article
                className="experienceExperiencias-card"
                key={experiencia.id}
              >
                <div className="experienceExperiencias-main">
                  <div className="experienceExperiencias-identity">
                    <div className="experienceExperiencias-image">
                      {experiencia.imagem_url ? (
                        <img
                          src={
                            experiencia.imagem_url
                          }
                          alt={
                            experiencia.titulo
                          }
                        />
                      ) : (
                        <Gift size={22} />
                      )}
                    </div>

                    <div>
                      <h2>
                        {
                          experiencia.titulo
                        }
                      </h2>

                      <p>
                        {experiencia.categoria_nome ||
                          "Sem categoria"}
                        {" · "}
                        {experiencia.fornecedor ||
                          "Fornecedor não informado"}
                      </p>

                      <div className="experienceExperiencias-tags">
                        {Array.isArray(
                          experiencia.pins
                        ) &&
                          experiencia.pins.map(
                            (pin) => (
                              <span
                                className="experienceExperiencias-tag"
                                key={pin.id}
                              >
                                {pin.nome}
                              </span>
                            )
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="experienceExperiencias-metric">
                    <span>
                      Valor de referência
                    </span>

                    <strong>
                      {formatarDinheiro(
                        experiencia
                          .valor_referencia
                      )}
                    </strong>
                  </div>

                  <div className="experienceExperiencias-metric">
                    <span>Validade</span>

                    <strong>
                      {experiencia
                        .validade_dias
                        ? `${experiencia.validade_dias} dias`
                        : "Não definida"}
                    </strong>
                  </div>

                  <div className="experienceExperiencias-metric">
                    <span>Disponibilidade</span>

                    <strong>
                      {experiencia
                        .quantidade_disponivel ===
                        null ||
                      experiencia
                        .quantidade_disponivel ===
                        undefined
                        ? "Ilimitada"
                        : Number(
                            experiencia
                              .quantidade_disponivel
                          )}
                    </strong>
                  </div>

                  <div className="experienceExperiencias-metric">
                    <span>Escolhas</span>

                    <strong>
                      {Number(
                        experiencia
                          .quantidade_escolhas ||
                          0
                      )}
                    </strong>
                  </div>

                  <span
                    className={`experienceExperiencias-status ${
                      experiencia.ativo
                        ? "ativa"
                        : "inativa"
                    }`}
                  >
                    {experiencia.ativo
                      ? "Ativa"
                      : "Inativa"}
                  </span>

                  <div className="experienceExperiencias-actions">
                    <button
                      type="button"
                      className="experienceExperiencias-editButton"
                      onClick={() =>
                        abrirEdicao(
                          experiencia
                        )
                      }
                    >
                      <Edit3 size={15} />
                      Editar
                    </button>

                    <button
                      type="button"
                      className="experienceExperiencias-deleteButton"
                      onClick={() =>
                        excluirExperiencia(
                          experiencia
                        )
                      }
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </article>
            )
          )}

          {!loading &&
            !experienciasFiltradas.length && (
              <div className="experienceExperiencias-empty">
                Nenhuma experiência encontrada.
              </div>
            )}

          {loading && (
            <div className="experienceExperiencias-empty">
              Carregando experiências...
            </div>
          )}
        </div>
      </section>

      {modalAberto && (
        <div
          className="experienceExperiencias-modalOverlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              fecharModal();
            }
          }}
        >
          <form
            className="experienceExperiencias-modal"
            onSubmit={salvarExperiencia}
          >
            <div className="experienceExperiencias-modalHeader">
              <div>
                <span className="experienceExperiencias-kicker">
                  Tikpag Experience
                </span>

                <h2>
                  {formulario.id
                    ? "Editar experiência"
                    : "Nova experiência"}
                </h2>
              </div>

              <button
                type="button"
                className="experienceExperiencias-closeButton"
                onClick={fecharModal}
              >
                <X size={19} />
              </button>
            </div>

            <div className="experienceExperiencias-modalBody">
              <div className="experienceExperiencias-formGrid">
                <label className="experienceExperiencias-field">
                  <span>
                    Título da experiência
                  </span>

                  <input
                    value={
                      formulario.titulo
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "titulo",
                        event.target.value
                      )
                    }
                    placeholder="Ex.: Noite no Cinema"
                    autoFocus
                  />
                </label>

                <label className="experienceExperiencias-field">
                  <span>Categoria</span>

                  <select
                    value={
                      formulario.categoria_id
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "categoria_id",
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Sem categoria
                    </option>

                    {categorias.map(
                      (categoria) => (
                        <option
                          key={categoria.id}
                          value={
                            categoria.id
                          }
                        >
                          {categoria.nome}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label className="experienceExperiencias-field">
                  <span>Fornecedor</span>

                  <input
                    value={
                      formulario.fornecedor
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "fornecedor",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceExperiencias-field">
                  <span>
                    Valor de referência
                  </span>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      formulario.valor_referencia
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "valor_referencia",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceExperiencias-field">
                  <span>
                    Quantidade disponível
                  </span>

                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={
                      formulario
                        .quantidade_disponivel
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "quantidade_disponivel",
                        event.target.value
                      )
                    }
                    placeholder="Vazio para ilimitada"
                  />
                </label>

                <label className="experienceExperiencias-field">
                  <span>
                    Validade em dias
                  </span>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={
                      formulario.validade_dias
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "validade_dias",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceExperiencias-field full">
                  <span>
                    Imagem da experiência
                  </span>

                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={
                      selecionarImagem
                    }
                    disabled={
                      salvando ||
                      enviandoImagem
                    }
                  />

                  {enviandoImagem && (
                    <small>
                      Enviando imagem...
                    </small>
                  )}

                  {formulario.imagem_url &&
                    !enviandoImagem && (
                      <div className="experienceExperiencias-imagePreview">
                        <img
                          src={
                            formulario.imagem_url
                          }
                          alt="Prévia da experiência"
                        />

                        <small>
                          {formulario.id
                            ? "Selecione outra imagem somente se quiser substituir a atual."
                            : "Imagem enviada com sucesso."}
                        </small>
                      </div>
                    )}
                </label>

                <label className="experienceExperiencias-field full">
                  <span>Descrição</span>

                  <textarea
                    value={
                      formulario.descricao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "descricao",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceExperiencias-field full">
                  <span>Instruções</span>

                  <textarea
                    value={
                      formulario.instrucoes
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "instrucoes",
                        event.target.value
                      )
                    }
                  />
                </label>

                <div className="experienceExperiencias-field full">
                  <span>
                    PINs vinculados
                  </span>

                  <div className="experienceExperiencias-pinGrid">
                    {pins.map((pin) => (
                      <label
                        className={`experienceExperiencias-pinOption ${
                          formulario.pins.includes(
                            Number(pin.id)
                          )
                            ? "selecionado"
                            : ""
                        }`}
                        key={pin.id}
                      >
                        <input
                          type="checkbox"
                          checked={
                            formulario.pins.includes(
                              Number(pin.id)
                            )
                          }
                          onChange={() =>
                            alternarPin(
                              Number(pin.id)
                            )
                          }
                        />

                        <span>
                          <strong>
                            {pin.nome}
                          </strong>

                          <small>
                            {formatarDinheiro(
                              pin.valor_minimo
                            )}
                          </small>
                        </span>
                      </label>
                    ))}

                    {!pins.length && (
                      <div className="experienceExperiencias-emptyPins">
                        Nenhum PIN disponível na
                        campanha selecionada.
                      </div>
                    )}
                  </div>
                </div>

                <label className="experienceExperiencias-check full">
                  <input
                    type="checkbox"
                    checked={
                      formulario.ativo
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "ativo",
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    <strong>
                      Experiência ativa
                    </strong>

                    <small>
                      Experiências inativas não
                      serão oferecidas em novas
                      escolhas.
                    </small>
                  </span>
                </label>
              </div>
            </div>

            <div className="experienceExperiencias-modalFooter">
              <button
                type="button"
                className="experienceExperiencias-secondaryButton"
                onClick={fecharModal}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="experienceExperiencias-primaryButton"
                disabled={
                    salvando ||
                    enviandoImagem
                  }
              >
                {salvando ? (
                  <RefreshCcw
                    size={16}
                    className="experienceExperiencias-spin"
                  />
                ) : (
                  <Gift size={16} />
                )}

                {enviandoImagem
                    ? "Enviando imagem..."
                    : formulario.id
                      ? "Salvar alterações"
                      : "Criar experiência"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}